import { type PowerSyncDrizzleDB, user } from "@listto/powersync";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";

/**
 * ── `user` is NOT excluded from `patchRowVerified` — audit 2026-08-13,
 * updated same day after `ThemeProvider` seeding guard
 * (commit `e72a1dd2`) ──
 *
 * `infra/guides/POWERSYNC_VERIFIED_WRITE.md` §4.1 excluded this table because
 * "row is created by server upon registration, so it is already in
 * checkpoint before the first preference". Verified and FALSE — and
 * remains so even though `ThemeProvider` is no longer part of problem (see
 * below):
 *
 *   - `db` (the PowerSync accessor) becomes non-null as soon as local SQLite
 *     local SQLite, not when initial sync finishes
 *     (`apps/web/src/providers/power-sync-context.tsx`: `connect()` is async
 *     and happens after opening the DB).
 *   - `userId()` becomes active as soon as `signUpActor`/`signInActor` receive
 *     the server session (`packages/shared/src/auth/controllers.ts`) — an
 *     independent REST call from the PowerSync stream.
 *
 * These two points alone are enough: between `db` + `userId()` becoming
 * ready and the initial checkpoint bringing the `user` row created by the
 * server, there is a window in which a normal `UPDATE` (changing language,
 * theme…) targets a row the local view does not yet have—it matches ZERO
 * rows and silently returns "success." This window does not depend on anyone
 * seeding a local row: it is simply the view synchronizing.
 *
 * (Historical context, now CLOSED) Until today a third factor existed
 * compounding this window for real accounts: `createEffect` in
 * `ThemeProvider` (`apps/web/src/providers/theme-provider.tsx`) inserted
 * locally a "phantom" 3-column `user` row for ANY `uid`,
 * without distinguishing guest from real account, racing with real row that
 * server had already created and was en route via sync. Commit
 * `e72a1dd2` closed that root cause: effect now only seeds
 * when `uid === VISITOR_ID` (see guard and rationale in
 * `theme-provider.tsx`). **This does NOT make protection of
 * for `user` unnecessary**—the two points above (`db` and
 * `userId()` ready before the checkpoint) still open the same window for any
 * early write, with or without a ghost row in the middle. Here, `patchRowVerified`
 * is generic checkpoint defense in depth, not a patch left behind from the
 * already-fixed bug — if it is ever removed, it should be because the window
 * above is proven not to exist, not because `ThemeProvider` stopped seeding
 * ghosts.
 *
 * Window identical to measurement in `habit-streak-app`/`boveda-vault-app`:
 * `patchRowVerified` writes → rereads → retries → throws instead of silently losing
 * a preference. See
 * `infra/reports/user-exclusion-audit/2026-08-13-summary.md`.
 *
 * ── Why ALL mutations below carry `.where(eq(user.id, userId))` ─
 *
 * Four of them (`locale`, `global_theme_id`, `is_blur`,
 * `follow_device_theme`) had this shape:
 *
 *     const query = db.update(user).set({ … });
 *     await (userId === VISITOR_ID ? query : query.where(eq(user.id, userId)));
 *
 * meaning that, in guest mode, the UPDATE went out **without `WHERE`** → it rewrote ALL
 * `user` rows in the local database. The branch existed for symmetry with
 * `getUserThemeSettingsQuery`, which for the guest reads with `.limit(1)` and no
 * filter; but a read `limit(1)` and an unfiltered UPDATE are not the same
 * bet: the former can pick the wrong row, while the latter crushes all of them.
 *
 * The premise behind it ("the guest database has only one
 * `user` row") is false in a specific window: during the guest→real upgrade, the
 * guest database CONNECTS to the real account's connector and
 * self-subscribes to all streams—including `organizations`, which syncs
 * one `user` row for EACH co-member of that account's organizations.
 * There the table has 1 + 1 + N rows, `user` is NOT `localOnly`, and each touched
 * row enqueues its own op in `ps_crud`.
 *
 * The correct filter is NOT guessed: `ThemeProvider`
 * (`apps/web/src/providers/theme-provider.tsx`) seeds the guest row with
 * `db.insert(user).values({ id: uid, … })`, and for a guest tab `uid` is exactly
 * `VISITOR_ID`. Thus `eq(user.id, userId)` points to the SAME row that the
 * unfiltered branch intended to touch when the table had only one row —
 * identical behavior in the normal case, bounded in the rest. It is also the
 * form already used by `updateUserFontFamilyMutation` and
 * `updateUserImageMutation`, which never had a guest branch.
 *
 * Since `patchRowVerified`, the `userId` filter no longer depends on each
 * mutation writing it manually: `idAndScopeCondition` ALWAYS applies
 * `eq(idColumn, id)` at minimum, so an UPDATE without `WHERE` on `user`
 * is no longer a form the code can take by accident.
 *
 * Guarded by `packages/shared/src/users/__tests__/mutations-where.test.ts`
 * (correct WHERE) and `mutations-verified-write.test.ts` (the window of
 * the checkpoint, including its CONTROL block).
 */

/**
 * Shared Drizzle mutation for updating User locale.
 */
export async function updateUserLocaleMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  locale: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { locale }, options);
}

/**
 * Shared Drizzle mutation for updating User font family preference.
 */
export async function updateUserFontFamilyMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  fontFamilyId: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { font_family: fontFamilyId }, options);
}

/**
 * Writes the avatar KEY to the local row.
 *
 * It is written local-first (like language, font, and theme), not through REST:
 * the new photo appears instantly and survives a cold reload while offline. The
 * BINARY does go through REST (`POST /api/avatar`) because it does not fit in
 * the sync queue; only the key returned by that upload travels through here.
 *
 * `null` = "remove the photo" (the initials return).
 *
 * A guest has no syncable row of its own and cannot have an avatar (zero rule),
 * so there is no `VISITOR_ID` branch here as there is for `locale`/`theme`.
 */
export async function updateUserImageMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  imageKey: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { image: imageKey }, options);
}

/**
 * Shared Drizzle mutation for updating global theme.
 */
export async function updateGlobalThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  themeId: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { global_theme_id: themeId }, options);
}

/**
 * Shared Drizzle mutation for updating blur effect preference.
 */
export async function updateBlurMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  isBlur: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { is_blur: isBlur ? 1 : 0 }, options);
}

/**
 * Shared Drizzle mutation for updating follow device appearance preference.
 */
export async function updateFollowDeviceThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  followDevice: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    user,
    user.id,
    userId,
    { follow_device_theme: followDevice ? 1 : 0 },
    options,
  );
}
